// Pure placement for anchored surfaces (tooltips, listboxes, menus):
// rects in, a position out. Flip to the other side when there is no room,
// then shift and clamp into the viewport. After common-shell's
// core/anchor.ts (ADR 0001 there): one placement function, never a second.
export interface Rect {
  top: number
  left: number
  width: number
  height: number
}

export type Side = 'top' | 'bottom'
export type Align = 'start' | 'center'

export interface Placement {
  top: number
  left: number
  side: Side
  /** Space available on the chosen side, for max-block-size. */
  available: number
}

export function placeAnchored(
  anchor: Rect,
  surface: { width: number; height: number },
  viewport: { width: number; height: number },
  options: { side?: Side; align?: Align; gap?: number; margin?: number } = {}
): Placement {
  const { side = 'bottom', align = 'center', gap = 6, margin = 8 } = options
  const below = viewport.height - (anchor.top + anchor.height) - gap - margin
  const above = anchor.top - gap - margin
  const fitsPreferred = side === 'bottom' ? surface.height <= below : surface.height <= above
  const chosen: Side = fitsPreferred ? side : below >= above ? 'bottom' : 'top'
  const available = Math.max(0, chosen === 'bottom' ? below : above)
  const height = Math.min(surface.height, available)
  const top = chosen === 'bottom' ? anchor.top + anchor.height + gap : anchor.top - gap - height

  const preferredLeft =
    align === 'start' ? anchor.left : anchor.left + anchor.width / 2 - surface.width / 2
  const maxLeft = viewport.width - margin - surface.width
  const left = Math.max(margin, Math.min(preferredLeft, maxLeft))

  return { top, left, side: chosen, available }
}

/** Rect of an element in client coordinates. */
export function rectOf(el: Element): Rect {
  const r = el.getBoundingClientRect()
  return { top: r.top, left: r.left, width: r.width, height: r.height }
}

/**
 * Re-run `update` on any scroll (capture phase, so nested scrollers count),
 * resize or size change of the anchor or surface while attached.
 */
export function observeMovement(anchor: Element, surface: Element, update: () => void): () => void {
  let frame = 0
  const schedule = () => {
    cancelAnimationFrame(frame)
    frame = requestAnimationFrame(update)
  }
  window.addEventListener('scroll', schedule, true)
  window.addEventListener('resize', schedule)
  const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule)
  ro?.observe(anchor)
  ro?.observe(surface)
  update()
  return () => {
    cancelAnimationFrame(frame)
    window.removeEventListener('scroll', schedule, true)
    window.removeEventListener('resize', schedule)
    ro?.disconnect()
  }
}
